import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useCurrentUser } from "./useCurrentUser";
import { useLogout } from "../logout/useLogout";
import { HiChevronDown, HiCog, HiUser, HiLogout, HiViewGrid, HiClock, HiShieldCheck } from "react-icons/hi";
import { getImageUrl } from "../../helper/imageHelper";
import ProfileModal from "../user/ProfileModal";

const UserDropdown = () => {
    const { user } = useCurrentUser();
    const { logout, isPending } = useLogout();
    const [isOpen, setIsOpen] = useState(false);

    // Profile modal state
    const [profileModalOpen, setProfileModalOpen] = useState(false);
    const [initialProfileTab, setInitialProfileTab] = useState("profile");


    const toggleDropdown = () => setIsOpen(!isOpen);
    const closeDropdown = () => setIsOpen(false);

    const openProfile = (tab = "profile") => {
        setInitialProfileTab(tab);
        setProfileModalOpen(true);
        closeDropdown();
    };

    const isAdmin = user?.role === "admin" || user?.isAdmin; // Check both for compatibility

    let avatar = user?.avatar || user?.user_metadata?.avatar;
    if (avatar && !avatar.includes("via.placeholder.com")) {
        avatar = getImageUrl(avatar);
    } else {
        avatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=random`;
    }

    return (
        <>
            <div className="relative">
                <button
                    onClick={toggleDropdown}
                    className="flex items-center gap-3 rounded-full border border-gray-200 bg-white py-1 pl-1 pr-3 transition-all hover:bg-gray-50 hover:shadow-sm dark:border-gray-700 dark:bg-zinc-800 dark:hover:bg-zinc-700"
                >
                    <img
                        src={avatar}
                        alt="User Avatar"
                        className="h-8 w-8 rounded-full object-cover ring-2 ring-gray-100 dark:ring-gray-700"
                    />
                    <span className="hidden text-sm font-medium text-gray-700 md:block dark:text-gray-200">
                        {user?.name || "User"}
                    </span>
                    <HiChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>

                <AnimatePresence>
                    {isOpen && (
                        <>
                            {/* Backdrop for closing */}
                            <div
                                className="fixed inset-0 z-40 h-full w-full cursor-default"
                                onClick={closeDropdown}
                            />

                            <motion.div
                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                transition={{ duration: 0.2 }}
                                className="absolute right-0 z-50 mt-2 w-64 origin-top-right rounded-xl bg-white p-2 shadow-xl ring-1 ring-black ring-opacity-5 dark:bg-zinc-900 dark:ring-white dark:ring-opacity-10"
                            >
                                <div className="mb-2 flex items-center gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
                                    <img
                                        src={avatar}
                                        alt={user?.name}
                                        className="h-10 w-10 rounded-full object-cover ring-2 ring-gray-100 dark:ring-gray-700"
                                    />
                                    <div className="overflow-hidden">
                                        <p className="truncate text-sm font-bold text-gray-900 dark:text-white">{user?.name}</p>
                                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
                                    </div>
                                </div>

                                <div className="space-y-1">
                                    {isAdmin && (
                                        <Link
                                            to="/dashboard"
                                            onClick={closeDropdown}
                                            className="flex w-full items-center gap-3 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-primary dark:text-gray-200 dark:hover:bg-zinc-800 dark:hover:text-primary"
                                        >
                                            <HiViewGrid className="h-5 w-5" />
                                            Dashboard
                                        </Link>
                                    )}

                                    <button
                                        onClick={() => openProfile("profile")}
                                        className="flex w-full items-center gap-3 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-primary dark:text-gray-200 dark:hover:bg-zinc-800 dark:hover:text-primary"
                                    >
                                        <HiUser className="h-5 w-5" />
                                        My Profile
                                    </button>

                                    <button
                                        onClick={() => openProfile("activity")}
                                        className="flex w-full items-center gap-3 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-primary dark:text-gray-200 dark:hover:bg-zinc-800 dark:hover:text-primary"
                                    >
                                        <HiClock className="h-5 w-5" />
                                        My Activity
                                    </button>

                                    <button
                                        onClick={() => openProfile("account")}
                                        className="flex w-full items-center gap-3 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-primary dark:text-gray-200 dark:hover:bg-zinc-800 dark:hover:text-primary"
                                    >
                                        <HiCog className="h-5 w-5" />
                                        Account Settings
                                    </button>

                                    <button
                                        onClick={() => openProfile("security")}
                                        className="flex w-full items-center gap-3 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-primary dark:text-gray-200 dark:hover:bg-zinc-800 dark:hover:text-primary"
                                    >
                                        <HiShieldCheck className="h-5 w-5" />
                                        Security Settings
                                    </button>
                                </div>

                                <div className="mt-2 border-t border-gray-100 pt-2 dark:border-gray-800">
                                    <button
                                        onClick={() => {
                                            logout();
                                            closeDropdown();
                                        }}
                                        disabled={isPending}
                                        className="flex w-full items-center gap-3 rounded-lg px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                                    >
                                        <HiLogout className="h-5 w-5" />
                                        {isPending ? "Logging out..." : "Sign out"}
                                    </button>
                                </div>
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>
            </div>

            {profileModalOpen && (
                <ProfileModal
                    onClose={() => setProfileModalOpen(false)}
                    initialTab={initialProfileTab}
                />
            )}
        </>
    );
};

export default UserDropdown;
